import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import MentionAutocomplete from '../components/MentionAutocomplete';
import CommentPreview from '../components/CommentPreview';
import { renderCommentText } from '../utils/commentUtils';
import styles from './PostDetailPage.module.css';

function PostDetailPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [commentPreviewUrls, setCommentPreviewUrls] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [authorHairstylistId, setAuthorHairstylistId] = useState(null);
  const [commentHairstylistMap, setCommentHairstylistMap] = useState({});
  const commentInputRef = useRef(null);
  const mentionContainerRef = useRef(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const postDoc = await getDoc(doc(db, 'posts', postId));
        if (postDoc.exists()) {
          setPost({ id: postDoc.id, ...postDoc.data() });
        }

        const commentsQuery = query(collection(db, 'comments'), where('postId', '==', postId));
        const commentsSnapshot = await getDocs(commentsQuery);
        const commentsData = commentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setComments(commentsData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };

    if (postId) {
      fetchPost();
    }
  }, [postId]);

  useEffect(() => {
    const urls = commentFiles.map((file) => URL.createObjectURL(file));
    setCommentPreviewUrls(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [commentFiles]);

  // Chercher le profil coiffeuse associé à l'auteur du post (si c'est une coiffeuse)
  useEffect(() => {
    let cancelled = false;

    const fetchHairstylistProfile = async () => {
      if (!post?.userId) {
        setAuthorHairstylistId(null);
        return;
      }

      try {
        const q = query(
          collection(db, 'hairstylists'),
          where('userId', '==', post.userId),
        );
        const snapshot = await getDocs(q);
        if (!cancelled && !snapshot.empty) {
          setAuthorHairstylistId(snapshot.docs[0].id);
        } else if (!cancelled) {
          setAuthorHairstylistId(null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching hairstylist for post author:', error);
        if (!cancelled) {
          setAuthorHairstylistId(null);
        }
      }
    };

    fetchHairstylistProfile();

    return () => {
      cancelled = true;
    };
  }, [post?.userId]);

  // Récupérer les IDs de profil coiffeuse pour les auteurs de commentaires
  useEffect(() => {
    let cancelled = false;

    const fetchHairstylistsForComments = async () => {
      try {
        const uniqueUserIds = Array.from(
          new Set(
            comments
              .map((c) => c.userId)
              .filter((id) => !!id),
          ),
        );

        if (!uniqueUserIds.length) {
          setCommentHairstylistMap({});
          return;
        }

        const batch = uniqueUserIds.slice(0, 10);
        const q = query(
          collection(db, 'hairstylists'),
          where('userId', 'in', batch),
        );
        const snapshot = await getDocs(q);
        if (cancelled) return;

        const map = {};
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.userId) {
            map[data.userId] = docSnap.id;
          }
        });
        setCommentHairstylistMap(map);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching hairstylists for comments (detail page):', error);
        if (!cancelled) {
          setCommentHairstylistMap({});
        }
      }
    };

    fetchHairstylistsForComments();

    return () => {
      cancelled = true;
    };
  }, [comments]);

  const displayName =
    currentUser?.displayName || currentUser?.email?.split('@')[0] || '';
  const isAdminUser = currentUser && displayName.toLowerCase() === 'admin';

  const handleComment = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if (!commentText.trim() && commentFiles.length === 0) return;

    try {
      let photoUrls = [];
      if (commentFiles.length > 0) {
        const uploadPromises = Array.from(commentFiles).map((file) => {
          if (!file.type.startsWith('image/')) return null;
          const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
          const storageRef = ref(
            storage,
            `commentPhotos/${postId}/${currentUser.uid}/${Date.now()}-${safeName}`,
          );
          return uploadBytes(storageRef, file).then((snapshot) =>
            getDownloadURL(snapshot.ref),
          );
        }).filter(Boolean);

        photoUrls = await Promise.all(uploadPromises);
      }

      await addDoc(collection(db, 'comments'), {
        postId,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email?.split('@')[0],
        text: commentText,
        photos: photoUrls,
        createdAt: serverTimestamp(),
      });
      setCommentText('');
      setCommentFiles([]);
      // Reload comments
      const commentsQuery = query(collection(db, 'comments'), where('postId', '==', postId));
      const commentsSnapshot = await getDocs(commentsQuery);
      const commentsData = commentsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setComments(commentsData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding comment:', error);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.loading}>Chargement...</div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.error}>Post introuvable</div>
        </div>
      </div>
    );
  }

  const handleOpenLightbox = (index, photos) => {
    const sourcePhotos = photos && photos.length > 0 ? photos : post.images;
    if (!sourcePhotos || sourcePhotos.length === 0) return;
    setLightboxPhotos(sourcePhotos);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const handlePrevLightbox = (event) => {
    event.stopPropagation();
    if (!lightboxPhotos || lightboxPhotos.length === 0) return;
    setLightboxIndex((prev) => (prev - 1 + lightboxPhotos.length) % lightboxPhotos.length);
  };

  const handleNextLightbox = (event) => {
    event.stopPropagation();
    if (!lightboxPhotos || lightboxPhotos.length === 0) return;
    setLightboxIndex((prev) => (prev + 1) % lightboxPhotos.length);
  };

  const handleStartEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.text || '');
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleSaveEditComment = async (comment) => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    const canEdit =
      comment.userId === currentUser.uid || isAdminUser;
    if (!canEdit) return;

    const newText = editingCommentText.trim();
    try {
      await updateDoc(doc(db, 'comments', comment.id), {
        text: newText,
      });
      setComments((prev) =>
        prev.map((c) =>
          c.id === comment.id ? { ...c, text: newText } : c,
        ),
      );
      setEditingCommentId(null);
      setEditingCommentText('');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating comment:', error);
    }
  };

  const handleDeleteComment = async (comment) => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    const canDelete =
      comment.userId === currentUser.uid || isAdminUser;
    if (!canDelete) return;

    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir supprimer ce commentaire ?',
    );
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'comments', comment.id));
      setComments((prev) => prev.filter((c) => c.id !== comment.id));

      // Mettre à jour le compteur de commentaires sur le post
      const postRef = doc(db, 'posts', postId);
      const postDoc = await getDoc(postRef);
      const currentCount = postDoc.data()?.commentsCount || 0;
      await updateDoc(postRef, {
        commentsCount: Math.max(0, currentCount - 1),
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting comment:', error);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <button type="button" className={styles.backBtn} onClick={() => navigate('/posts')}>
          ← Retour
        </button>
        <div className={styles.postCard}>
          <div className={styles.postHeader}>
            <div className={styles.postAuthor}>
              <button
                type="button"
                className={styles.authorAvatarButton}
                onClick={() => {
                  if (authorHairstylistId) {
                    navigate(`/hairstylist/${authorHairstylistId}`);
                  }
                }}
                disabled={!authorHairstylistId}
              >
                <div className={styles.authorAvatar}>
                  {post.authorName?.[0] || 'U'}
                </div>
              </button>
              <div>
                {authorHairstylistId ? (
                  <button
                    type="button"
                    className={styles.authorNameButton}
                    onClick={() => navigate(`/hairstylist/${authorHairstylistId}`)}
                  >
                    {post.authorName || 'Utilisateur'}
                  </button>
                ) : (
                  <h2>{post.authorName || 'Utilisateur'}</h2>
                )}
                <p className={styles.authorLocation}>{post.authorLocation || ''}</p>
              </div>
            </div>
          </div>
          {post.text && <p className={styles.postText}>{post.text}</p>}
          {post.images && post.images.length > 0 && (
            <div className={styles.postImages}>
              {post.images.map((img, idx) => (
                <button
                  key={img}
                  type="button"
                  className={styles.postImageWrapper}
                  onClick={() => handleOpenLightbox(idx, post.images)}
                >
                  <img src={img} alt={`Post ${idx + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.commentsSection}>
          <h3>
            {comments.length === 1 ? '1 commentaire' : `${comments.length} commentaires`}
          </h3>
          <div className={styles.commentsList}>
            {comments.map((comment) => (
              <div key={comment.id} className={styles.comment}>
                <div className={styles.commentAuthor}>
                  {commentHairstylistMap[comment.userId] ? (
                    <button
                      type="button"
                      className={styles.commentAuthorHairstylist}
                      onClick={() => navigate(`/hairstylist/${commentHairstylistMap[comment.userId]}`)}
                    >
                      {comment.userName || 'Utilisateur'}
                    </button>
                  ) : (
                    <strong>{comment.userName || 'Utilisateur'}</strong>
                  )}
                </div>
                {editingCommentId === comment.id ? (
                  <input
                    type="text"
                    className={styles.commentEditInput}
                    value={editingCommentText}
                    onChange={(e) => setEditingCommentText(e.target.value)}
                  />
                ) : (
                  <p>
                    {renderCommentText(comment.text || '').map((part, idx) => {
                      if (part.type === 'link') {
                        return (
                          <a
                            key={idx}
                            href={part.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.commentLink}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {part.content}
                          </a>
                        );
                      }
                      if (part.type === 'mention') {
                        return (
                          <Link
                            key={idx}
                            to={`/hairstylist/${part.id}`}
                            className={styles.commentMention}
                            onClick={(e) => e.stopPropagation()}
                          >
                            @{part.name}
                          </Link>
                        );
                      }
                      return <span key={idx}>{part.content}</span>;
                    })}
                  </p>
                )}
                {comment.photos && comment.photos.length > 0 && (
                  <div className={styles.commentPhotos}>
                    {comment.photos.map((url, index) => (
                      <button
                        key={url}
                        type="button"
                        className={styles.commentPhotoWrapper}
                        onClick={() => handleOpenLightbox(index, comment.photos)}
                      >
                        <img src={url} alt={`Photo ${index + 1} du commentaire`} />
                      </button>
                    ))}
                  </div>
                )}
                <div className={styles.commentActions}>
                  {currentUser && (comment.userId === currentUser.uid || isAdminUser) && (
                    <div className={styles.commentActionButtons}>
                      {editingCommentId === comment.id ? (
                        <>
                          <button
                            type="button"
                            className={styles.commentAction}
                            onClick={() => handleSaveEditComment(comment)}
                          >
                            Enregistrer
                          </button>
                          <button
                            type="button"
                            className={styles.commentAction}
                            onClick={handleCancelEditComment}
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className={styles.commentAction}
                            onClick={() => handleStartEditComment(comment)}
                          >
                            Modifier
                          </button>
                          <button
                            type="button"
                            className={styles.commentAction}
                            onClick={() => handleDeleteComment(comment)}
                          >
                            Supprimer
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <form className={styles.commentForm} onSubmit={handleComment}>
            <div ref={mentionContainerRef} className={styles.commentInputWrapper}>
              <input
                ref={commentInputRef}
                type="text"
                placeholder="Ajouter un commentaire..."
                value={commentText}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setCommentText(newValue);
                  
                  // Vérifier si on doit afficher l'autocomplétion
                  const mentionMatch = newValue.match(/@(\w{3,})$/);
                  if (mentionMatch && commentInputRef.current) {
                    const rect = commentInputRef.current.getBoundingClientRect();
                    const containerRect = mentionContainerRef.current?.getBoundingClientRect();
                    setMentionPosition({
                      top: rect.bottom - (containerRect?.top || 0) + 5,
                      left: rect.left - (containerRect?.left || 0),
                    });
                    setShowMentionAutocomplete(true);
                  } else {
                    setShowMentionAutocomplete(false);
                  }
                }}
                className={styles.commentInput}
              />
              {showMentionAutocomplete && (
                <MentionAutocomplete
                  value={commentText}
                  onChange={setCommentText}
                  position={mentionPosition}
                  inputRef={commentInputRef}
                  onSelect={() => setShowMentionAutocomplete(false)}
                />
              )}
              {commentText && (
                <CommentPreview text={commentText} />
              )}
            </div>
            <div className={styles.commentPhotoInputRow}>
              <input
                id={`detail-comment-photos-${postId}`}
                type="file"
                accept="image/*"
                multiple
                className={styles.commentFileInputHidden}
                onChange={(e) => {
                  const selected = Array.from(e.target.files || []);
                  if (selected.length === 0) return;
                  setCommentFiles((prev) => {
                    const existingKeys = new Set(
                      prev.map((f) => `${f.name}_${f.lastModified}`),
                    );
                    const merged = [
                      ...prev,
                      ...selected.filter(
                        (file) =>
                          file.type.startsWith('image/') &&
                          !existingKeys.has(`${file.name}_${file.lastModified}`),
                      ),
                    ];
                    return merged.slice(0, 3);
                  });
                }}
              />
              <label
                htmlFor={`detail-comment-photos-${postId}`}
                className={styles.commentPhotoIconButton}
                aria-label="Ajouter des photos"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 7H7L9 4H15L17 7H20C21.1046 7 22 7.89543 22 9V19C22 20.1046 21.1046 21 20 21H4C2.89543 21 2 20.1046 2 19V9C2 7.89543 2.89543 7 4 7Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="13"
                    r="3.5"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </label>
            </div>
            {commentPreviewUrls.length > 0 && (
              <div className={styles.commentPreviewRow}>
                {commentPreviewUrls.map((url, index) => (
                  <div key={url} className={styles.commentPreview}>
                    <img src={url} alt={`Prévisualisation ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
            <button
              type="submit"
              className={styles.commentBtn}
              disabled={!commentText.trim() && commentFiles.length === 0}
            >
              Publier
            </button>
          </form>
        </div>
      </div>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {lightboxOpen && lightboxPhotos && lightboxPhotos.length > 0 && (
        <div
          className={styles.lightboxOverlay}
          onClick={handleCloseLightbox}
          role="dialog"
          aria-modal="true"
        >
          <div
            className={styles.lightboxContent}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.lightboxClose}
              onClick={handleCloseLightbox}
              aria-label="Fermer la photo"
            >
              ×
            </button>
            {lightboxPhotos.length > 1 && (
              <>
                <button
                  type="button"
                  className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
                  onClick={handlePrevLightbox}
                  aria-label="Photo précédente"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={`${styles.lightboxNav} ${styles.lightboxNext}`}
                  onClick={handleNextLightbox}
                  aria-label="Photo suivante"
                >
                  ›
                </button>
              </>
            )}
            <img
              src={lightboxPhotos[lightboxIndex]}
              alt={`Photo ${lightboxIndex + 1} sur ${lightboxPhotos.length}`}
              className={styles.lightboxImage}
            />
            {lightboxPhotos.length > 1 && (
              <div className={styles.lightboxCounter}>
                {lightboxIndex + 1} / {lightboxPhotos.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PostDetailPage;

